/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Scripted answers for the
 * "Ask about bids" panel on RFP-2026-014. Figures come from the same data the
 * grid and scoring tabs use, so the answers cannot drift from the numbers.
 * Citations point at the bid documents authored in sourcing-bid-documents.ts.
 */
import type { ScriptedAnswer } from '../../ask/types';
import {
  BASELINE_THREE_YEAR,
  BIDS,
  DUAL_SOURCE_PREMIUM,
  NET45_WORKING_CAPITAL,
  RECOMMENDED_RESULT,
  INCUMBENT_RESULT,
  FEE_AVOIDED_THREE_YEAR,
  landedPerUnit,
  recommendedAnnualSpend,
  scoresFor,
  threeYearCost,
} from './sourcing-data';
import { BID_DOC_IDS, RFP_DOC_ID } from './sourcing-bid-documents';
import { fmtMillions, fmtPerUnit, fmtThousands } from './sourcing-format';
import { DEFAULT_WEIGHTS, weightedTotal } from './sourcing-scoring';

const brightpak = BIDS['sup-brightpak'];
const brightpakScore = weightedTotal(
  scoresFor('sup-brightpak'),
  DEFAULT_WEIGHTS,
);
const brightpakLanded = landedPerUnit(brightpak);
const brightpakThreeYear = threeYearCost(brightpak);
const aureliaLanded = landedPerUnit(BIDS['sup-aurelia']);
const northgateLanded = landedPerUnit(BIDS['sup-northgate']);
const northgateAnnual = recommendedAnnualSpend('sup-northgate');
const net60Cash = northgateAnnual * (30 / 365);

const NORTHGATE = BID_DOC_IDS['sup-northgate'];
const AURELIA = BID_DOC_IDS['sup-aurelia'];
const HANOVER = BID_DOC_IDS['sup-hanover'];
const BRIGHTPAK = BID_DOC_IDS['sup-brightpak'];

export const BID_SUGGESTIONS: ScriptedAnswer[] = [
  {
    question: "What in Northgate's bid is superfluous?",
    aliases: [
      'What can we strike from the Northgate bid',
      'Which Northgate fees can we remove',
      'What is unnecessary in the Northgate bid',
      "What's superfluous in Northgate's bid",
    ],
    thinking: [
      'Reading Northgate bid: pricing schedule and terms',
      'Checking each charge against the bid request pricing rules',
      'Comparing with the Pillar Pro finish spec and current agreement',
    ],
    answer: `Three items in Northgate's bid are not needed to meet the bid scope. The account team alone is **${fmtThousands(FEE_AVOIDED_THREE_YEAR)}** of avoidable cost over three years.

- **Dedicated account team, $48,000 per year.** Billed on top of the unit price and tied to Schedule C service levels. Pillar already has a named account manager and quarterly business reviews, and the bid request says only listed charges are payable. Ask for it to be removed.
- **Finish upcharges, $1.40 to $2.60 per fixture.** Pillar Pro ships in a single standard white housing finish, so these would never apply, but they would sit in the contract as chargeable adders. Strike them.
- **Automatic 12-month renewal.** A rolling renewal on 90 days of notice would carry Net 30 and a 120-day notice period for changes past the term. Replace it with a fixed three-year term.

Negotiable, but only paid when used: rush production at plus 8%, and $1,800 per nameplate or carton artwork change.

Off-policy rather than superfluous: Net 30 against Net 60, and 120 days of notice before changes against the 180-day requirement.`,
    citations: [
      {
        documentId: NORTHGATE,
        ref: '2',
        quote: 'dedicated account team, $48,000 per year billed quarterly',
      },
      { documentId: NORTHGATE, ref: '1' },
      { documentId: NORTHGATE, ref: '7' },
      {
        documentId: RFP_DOC_ID,
        ref: '2',
        quote: 'charges that are not listed are not payable',
      },
    ],
  },
  {
    question: "Why not award to Brightpak if it's the cheapest?",
    aliases: [
      'Why not award to Brightpak',
      'Why is Brightpak excluded',
      'Why is Brightpak not recommended',
      'Brightpak has the lowest price, why not pick them',
    ],
    thinking: [
      'Reading Brightpak bid: pricing, certificates and terms',
      'Rebuilding delivered cost with the 30% tariff',
      'Checking requirements in bid request sections 4 and 6',
    ],
    answer: `Brightpak is cheapest on **unit price** (${fmtPerUnit(brightpak.unitPrice)} per fixture) and the most expensive once everything is delivered.

- **Delivered cost ${fmtPerUnit(brightpakLanded)} against ${fmtPerUnit(aureliaLanded)} for Aurelia.** The 30% tariff adds ${fmtPerUnit(brightpak.tariff)} per fixture and freight another ${fmtPerUnit(brightpak.freight)}. At full volume that is **${fmtMillions(brightpakThreeYear)} over three years against ${fmtMillions(BASELINE_THREE_YEAR)}** at today's prices, an increase of ${fmtMillions(brightpakThreeYear - BASELINE_THREE_YEAR)}.
- **Forced-labor import risk.** An aluminium supplier behind the housings is a possible match on the US forced-labor import ban list (RSK-307) and still needs review. Brightpak attested for itself only.
- **Misses mandatory requirements.** The UL and DLC Premium certificates are not yet held (expected Q1 2027), the light lifetime is 60,000 hours against the 100,000 minimum, and notice before changes is 30 days against the 180-day requirement.
- **Weak commercial terms.** Net 30, a 60-day price validity against the 120-day minimum, and tariff changes passed through immediately.

Brightpak scores ${brightpakScore.toFixed(1)} out of 10 on the default weights and ranks last. Keep it as a price benchmark only.`,
    citations: [
      {
        documentId: BRIGHTPAK,
        ref: '2',
        quote: 'the tariff adds $24.54 per fixture',
      },
      { documentId: BRIGHTPAK, ref: '4' },
      { documentId: BRIGHTPAK, ref: '6' },
      { documentId: BRIGHTPAK, ref: '7' },
      { documentId: RFP_DOC_ID, ref: '4' },
      { documentId: RFP_DOC_ID, ref: '6' },
    ],
  },
  {
    question:
      'Which bidders meet our 180-day notice-before-changes requirement?',
    aliases: [
      'Who meets the 180-day notice before changes requirement',
      'Which bidders accept 180 days of notice before changes',
      'Who meets our notice before changes requirement',
    ],
    thinking: [
      'Reading bid request section 4: quality and certification requirements',
      'Checking section 4 and section 7 of each bid',
    ],
    answer: `Two of four bidders meet the 180-day requirement as submitted.

- **Aurelia, 180 days.** Meets it and mirrors the current agreement.
- **Hanover, 180 days.** Meets it, but has no performance history with Pillar.
- **Northgate, 120 days.** Sixty days short, and listed as an exception. This is a negotiation target and a condition of the award.
- **Brightpak, 30 days.** Far below the requirement, with no mitigation offered.

bid request section 4 makes 180 days mandatory because a change of LED package, driver or housing material can invalidate the UL certification, so the recommended 60/40 split depends on Northgate moving to 180 days in the contract.`,
    citations: [
      {
        documentId: RFP_DOC_ID,
        ref: '4',
        quote: 'at least 180 days of prior written notice',
      },
      { documentId: AURELIA, ref: '4' },
      { documentId: HANOVER, ref: '4' },
      { documentId: NORTHGATE, ref: '4' },
      { documentId: NORTHGATE, ref: '7' },
      { documentId: BRIGHTPAK, ref: '4' },
    ],
  },
  {
    // Worded so the AR demo's "compare ... terms" prompt does not take it in Nami.
    question: 'What payment terms did each bidder offer?',
    aliases: [
      'Compare payment terms across bidders',
      'How do the payment terms compare',
      'Compare bidder payment terms',
      'Which bidders accept Net 60',
    ],
    thinking: [
      'Reading bid request section 3: payment and commercial terms',
      'Collecting the payment exceptions listed in each bid',
    ],
    answer: `Only Aurelia meets the Net 60 policy in bid request section 3.

- **Aurelia, Net 60** with the 2% early-payment discount (paid within 10 days) kept.
- **Hanover, Net 45**, listed as an exception.
- **Northgate, Net 30.** States it does not accept Net 60 on new agreements.
- **Brightpak, Net 30**, together with a 60-day price validity.

Cash: moving Northgate from Net 30 to Net 45 on its 40% share (about ${fmtMillions(northgateAnnual)} per year) releases roughly ${fmtThousands(NET45_WORKING_CAPITAL)} of cash. Net 60 would release about ${fmtThousands(net60Cash)}.`,
    citations: [
      {
        documentId: RFP_DOC_ID,
        ref: '3',
        quote: 'standard payment terms are Net 60',
      },
      { documentId: AURELIA, ref: '7' },
      { documentId: HANOVER, ref: '7' },
      { documentId: NORTHGATE, ref: '7' },
      { documentId: BRIGHTPAK, ref: '7' },
    ],
  },
  {
    question: 'What does using two suppliers cost compared with Aurelia alone?',
    aliases: [
      'How much more is the split than Aurelia only',
      'What is the cost of using two suppliers',
      'Why not give Aurelia 100%',
    ],
    thinking: [
      'Reading both pricing schedules',
      'Comparing the 60/40 split with a single-source award',
      'Checking the single-supplier cap in bid request section 5',
    ],
    answer: `Awarding 100% to Aurelia would be about **${fmtThousands(DUAL_SOURCE_PREMIUM)} cheaper over three years** than the 60/40 split (${fmtMillions(INCUMBENT_RESULT.threeYearCost, 2)} against ${fmtMillions(RECOMMENDED_RESULT.threeYearCost, 2)}), because Northgate's delivered cost is ${fmtPerUnit(northgateLanded - aureliaLanded)} higher per fixture.

That premium is what buys resilience.

- Aurelia alone would exceed the 60% single-supplier cap in bid request section 5.
- Both suppliers can serve the Dallas, Phoenix and Hayward DCs, so either one can cover a disruption at the other.
- Northgate is the only domestic source, with a 4-week lead time.

The premium is under 1% of three-year spend.`,
    citations: [
      { documentId: AURELIA, ref: '2' },
      { documentId: NORTHGATE, ref: '2' },
      {
        documentId: RFP_DOC_ID,
        ref: '5',
        quote: 'caps any single supplier at 60%',
      },
    ],
  },
];
